// Ported from microsites/womens-conference/src/components/Countdown.tsx.
// The one change: "now" is handed in (the experience's clock, which the time
// machine can move) instead of read from Date.now, and both are MP wall clock.
import { wallDiff } from '@/lib/stages';

function parts(msLeft: number) {
  const days = Math.floor(msLeft / 86_400_000);
  const hours = Math.floor((msLeft % 86_400_000) / 3_600_000);
  const minutes = Math.floor((msLeft % 3_600_000) / 60_000);
  return { days, hours, minutes };
}

/**
 * Days, hours, minutes. No seconds: a conference is not a rocket launch, and a
 * ticking seconds column makes people watch the timer instead of reading the
 * page.
 *
 * The caption sits ABOVE the numbers. It is the context, and a reader who
 * stops after one line should still know what the clock is counting; under the
 * numbers it was a footnote to information she had already had to guess at.
 *
 * What it counts toward matters more than how it looks. While seats are
 * selling, the deadline that can actually be missed is registration closing —
 * a big beautiful clock ticking down to a November weekend does not tell a
 * woman in September that she has eleven days to decide. So the clock counts to
 * the close and the weekend itself becomes the quiet line underneath: present,
 * unmissable in meaning, not competing for the same glance.
 *
 * `urgentDays` is the one bit of pressure the page is allowed. Under that many
 * days the numbers warm to marigold. It is not a red banner and it never says
 * "hurry" — the number is already saying it.
 */
export function Countdown({
  now,
  target,
  startedLabel,
  caption,
  secondary,
  urgentDays = 7,
}: {
  /** Wall clock now. */
  now: string;
  /** Wall clock, no offset. */
  target: string;
  startedLabel: string;
  /** Reads into the numbers, so phrase it as a lead-in ("Doors open in")
   *  rather than a tail ("until doors open"). */
  caption?: string;
  /** The other date worth knowing, kept subordinate on purpose. */
  secondary?: { target: string; label: string };
  urgentDays?: number;
}) {
  const msLeft = wallDiff(target, now);
  const secondaryDays = secondary
    ? Math.max(0, Math.ceil(wallDiff(secondary.target, now) / 86_400_000))
    : null;

  const tail =
    secondaryDays !== null && secondary ? (
      <p className="text-wm-sage-200/80 mt-3 text-sm">
        <span className="text-wm-cream tabular-nums">{secondaryDays}</span> {secondary.label}
      </p>
    ) : null;

  if (msLeft <= 0) {
    return (
      <div>
        <p className="wm-display text-wm-cream text-2xl italic">{startedLabel}</p>
        {tail}
      </div>
    );
  }

  const { days, hours, minutes } = parts(msLeft);
  const urgent = days < urgentDays;
  const cell = (value: number, unit: string) => (
    <span className="flex flex-col items-center">
      <span
        className={`wm-display text-4xl tabular-nums sm:text-5xl ${
          urgent ? 'text-wm-marigold' : 'text-wm-cream'
        }`}
      >
        {value}
      </span>
      <span className="wm-kicker wm-kicker-on-dark mt-1">{unit}</span>
    </span>
  );

  return (
    <div>
      {caption ? (
        <p className={`wm-kicker mb-2 ${urgent ? 'text-wm-marigold' : 'kicker-on-dark'}`}>
          {caption}
        </p>
      ) : null}
      {/* Three equal columns, so the middle one straddles the centre line. With
          flex the cells took their own widths and "minutes" being wider than
          "days" pushed the hours column left of centre, which read as the whole
          clock being off. */}
      <div
        className="grid grid-cols-3 items-start gap-6"
        role="timer"
        aria-label={
          caption
            ? `${caption} ${days} days, ${hours} hours, ${minutes} minutes`
            : `${days} days, ${hours} hours, ${minutes} minutes remaining`
        }
      >
        {cell(days, 'days')}
        {cell(hours, 'hours')}
        {cell(minutes, 'minutes')}
      </div>
      {tail}
    </div>
  );
}
